import {z} from "zod";

import {userPropertyService} from "../../core/services/user-property";
import {normalizeUsPhoneDigits} from "../../properties/lib/us-phone";

export const vehicleObjectSchema = z.object({
  make: z.string(),
  model: z.string(),
  color: z.string(),
  state: z.string(),
  plate: z.string(),
  year: z.preprocess((val) => {
    if (val === "" || val === undefined || val === null) return undefined;
    if (typeof val === "number" && Number.isNaN(val)) return undefined;
    const n = typeof val === "number" ? val : parseInt(String(val), 10);
    return Number.isFinite(n) ? n : undefined;
  }, z.number().min(1900).max(new Date().getFullYear()).optional()),
});

export function refineVehicleCommonFields(
  data: {
    make: string;
    model: string;
    color: string;
    state: string;
    plate: string;
    year?: number;
  },
  ctx: z.RefinementCtx,
) {
  if (!data.make?.trim()) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: "Make is required",
      path: ["make"],
    });
  }
  if (!data.model?.trim()) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: "Model is required",
      path: ["model"],
    });
  }
  if (!data.color?.trim()) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: "Color is required",
      path: ["color"],
    });
  }
  if (!data.state?.trim()) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: "State is required",
      path: ["state"],
    });
  }
  if (!data.plate?.trim()) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: "License plate is required",
      path: ["plate"],
    });
  }
}

export const vehicleSchema = vehicleObjectSchema.superRefine(refineVehicleCommonFields);

const householdFormShape = z.object({
  house_hold_id: z.number(),
  name: z.string(),
  // street: z.string(),
  // number: z.number(),
  // state: z.string(),
  // city: z.string(),
  // zipcode: z.string(),
  // user_id: z.number().optional(),
});

const datesRangeShape = z
  .object({
    from: z.date().optional(),
    to: z.date().optional(),
  })
  .optional();

function countDaysInRange(from: Date, to: Date): number {
  const start = new Date(from);
  start.setHours(0, 0, 0, 0);
  const end = new Date(to);
  end.setHours(0, 0, 0, 0);
  const diff = Math.round((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24));
  return diff + 1;
}

function toYyyyMmDd(d: Date): string {
  return d.toISOString().slice(0, 10);
}

function isDateInRange(dateStr: string, from: Date, to: Date): boolean {
  const fromStr = toYyyyMmDd(from);
  const toStr = toYyyyMmDd(to);
  return dateStr >= fromStr && dateStr <= toStr;
}

export const safelistingFormSchema = z
  .object({
    household: householdFormShape.nullable(),
    dates_range: datesRangeShape,
    recipient_name: z.string(),
    recipient_email: z.string(),
    recipient_phone: z.string(),
    notes: z.string(),
    private_notes: z.string(),
    excluded_dates: z.array(z.string()),
    override_count_toward_total: z.boolean(),
    override_days_in_a_row: z.boolean(),
    override_limit: z.boolean(),
    override_reason: z.string().optional(),
  })
  .superRefine((data, ctx) => {
    const maxSafelistingsInARow = userPropertyService.getMaxSafelistingsInARow();

    if (data.household == null) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Household is required",
        path: ["household"],
      });
    }
    const from = data.dates_range?.from;
    const to = data.dates_range?.to;
    if (!from || !to) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Date range is required",
        path: ["dates_range"],
      });
    } else {
      const totalDays = countDaysInRange(from, to);
      const remaining = totalDays - data.excluded_dates.length;
      if (remaining < 1) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "At least one day must remain in the range",
          path: ["excluded_dates"],
        });
      }
      if (
        maxSafelistingsInARow > 0 &&
        remaining > maxSafelistingsInARow &&
        !data.override_days_in_a_row
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: `You can only create ${maxSafelistingsInARow} safelistings in a row`,
          path: ["dates_range"],
        });
      }
      if (from > to) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "End date must be on or after start date",
          path: ["dates_range"],
        });
      }
      for (const excluded of data.excluded_dates) {
        if (!isDateInRange(excluded, from, to)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: `Excluded date ${excluded} must be within the selected range`,
            path: ["excluded_dates"],
          });
        }
      }
    }

    if (!data.recipient_name.trim()) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Recipient name is required",
        path: ["recipient_name"],
      });
    }

    if (data.recipient_email.trim()) {
      const emailSchema = z.string().email("Invalid email");
      const result = emailSchema.safeParse(data.recipient_email);
      if (!result.success) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: result.error.errors[0]?.message ?? "Invalid email",
          path: ["recipient_email"],
        });
      }
    } else {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Recipient email is required",
        path: ["recipient_email"],
      });
    }

    const recipientPhoneDigits = normalizeUsPhoneDigits(data.recipient_phone ?? "");
    if (recipientPhoneDigits.length > 0 && recipientPhoneDigits.length !== 10) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Recipient phone must be 10 digits",
        path: ["recipient_phone"],
      });
    }

    if (data.override_limit) {
      const reason = data.override_reason?.trim() ?? "";
      if (reason.length < 3) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Override reason must be at least 3 characters",
          path: ["override_reason"],
        });
      }
    }
  });
